/* eslint-disable jsx-a11y/no-static-element-interactions */
// @ts-nocheck

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */

/* eslint-disable jsx-a11y/click-events-have-key-events */
// styles
import ComparisonCheckbox from '@jo-v1/components/CompareCheckbox';
import { SchoolConfigData } from '@jo-v1/components/ExitStrategy/type';
import ProductFlags from '@jo-v2/components/ProductFlags';
import ProductTags from '@jo-v2/components/ProductTags';
import { useEffect, useState, useMemo, memo, type MouseEventHandler, useRef } from 'react';
import { FaStar, FaThumbsUp } from 'react-icons/fa';
import { HiLocationMarker } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useProductEvents from '@core/hooks/cohesion/useProductEvents';
import useIntersectionObserver from '@core/hooks/useIntersectionObserver';
// selectors, actions
import {
  selectRecommenderPopupListId,
  setHeclidMapEntry,
  setProductCorrelationIdMapEntry,
  setViewCorrelationIdMapEntry,
  selectHeclidMap,
  selectProductCorrelationIdMap,
} from '@core/reducers/eventingSlice';
import { selectAllInputs } from '@core/reducers/inputsSlice';
import {
  setLeadDelivery,
  selectLeadDelivery,
  setCurrentProgram,
  selectSubmissions,
  setShowDuplicatePopup,
  selectIsRecommenderPopupOpen,
} from '@core/reducers/matchesSlice';
import { mobiusGetApplicationCta, mobiusRedirect } from '@core/services/mobius';
import { getSchoolConfig } from '@core/services/schoolConfig';
import type { VoyagerResult } from '@core/ts/results';
// hooks & utils
import cn from '@core/utils/classNames';
import getToken from '@core/utils/getToken';

import SchoolProgramTabs from '../SchoolProgramTabs';
import styles from './ResultHighlightCard.module.css';

type ResultHighlightCardProps = {
  result: VoyagerResult;
  setRecommenderStep?: (step: string) => void;
  position?: number;
  setLoading?: (loading: boolean) => void;
  eventingOverrides?: any;
  index?: number;
  withTopBorder?: boolean;
  fullWidth?: boolean;
  onRecommender?: boolean;
};

const ResultHighlightCard = ({
  result,
  setRecommenderStep,
  position,
  setLoading,
  eventingOverrides,
  index,
  fullWidth = false,
  withTopBorder = true,
  onRecommender = true,
}: ResultHighlightCardProps) => {
  const { currentPrograms } = useSelector(selectLeadDelivery);

  // dispatches actions
  const dispatch = useDispatch();
  const cardRef = useRef<HTMLElement>(null);
  const isInView = useIntersectionObserver(cardRef);
  const [hasBeenViewed, setHasBeenViewed] = useState(false);
  const eventsHaveFired = useRef(false);

  // local state
  const [schoolConfig, setSchoolConfig] = useState<SchoolConfigData>({});
  const [applicationCtaData, setApplicationCtaData] = useState<{ label: string; url: string } | null>(null);
  // inputs state properties
  const inputs = useSelector(selectAllInputs);

  // Eventing Ids
  const popupListId = useSelector(selectRecommenderPopupListId);
  const newViewCorrelationId = useMemo(() => uuid(), []);

  // Store
  const heclidMap = useSelector(selectHeclidMap);
  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // determine if we are in single submission
  // DEV NOTE: setting this to just be true for the purposes of a desired optimization
  // and we don't want to remove the multi-select code just yet
  const isRecommenderPopupOpen = useSelector(selectIsRecommenderPopupOpen);
  const flags = useFeatureFlags();
  const isComparisonTest = !!flags.programsComparisonTest;

  // Initialize events
  const { productLoaded, productViewed, productClicked } = useProductEvents({
    listId: popupListId,
    viewCorrelationId: newViewCorrelationId,
    product: {
      sku: String(result?.cap?.id),
      variant: result?.program?.degree?.slug,
      productId: String(result?.program?.id),
      name: result?.program?.subject?.slug,
      category: result?.program?.category?.slug,
      brand: result?.school?.slug,
      position,
      location: 'voyager-recommender-embedded',
      formatType: 'app',
      formatSubtype: 'jo-grid',
      positionEngine: 'algolia',
      ...eventingOverrides?.product,
    },
    customDimensions: eventingOverrides?.customDimensions,
  });

  useEffect(() => {
    // Check both the state and ref to ensure events only fire once
    if (isInView && !hasBeenViewed && !eventsHaveFired.current && !isRecommenderPopupOpen) {
      // Set both ref and state immediately
      eventsHaveFired.current = true;
      setHasBeenViewed(true);

      // Fire events after setting flags to prevent double firing
      productViewed();
      productLoaded();

      if (result?.program?.id) {
        dispatch(
          setViewCorrelationIdMapEntry({
            key: result.program.id,
            value: newViewCorrelationId,
          })
        );
      }
    }
  }, [
    isInView,
    hasBeenViewed,
    productViewed,
    productLoaded,
    dispatch,
    result?.program?.id,
    newViewCorrelationId,
    isRecommenderPopupOpen,
  ]);

  useEffect(() => {
    const getConfig = async () => {
      const schoolConfig = await getSchoolConfig(result?.school.id);
      setSchoolConfig(schoolConfig);
    };
    getConfig();
  }, [result]);

  const onBtnClickHandler = async (event) => {
    // if (!event.target.classList.contains('tab-trigger')) {
    setLoading(true);
    dispatch(setCurrentProgram(result));

    const newProductCorrelationId = uuid();

    // add correlationId to map for LDS lead submission
    if (result?.program?.id) {
      dispatch(
        setProductCorrelationIdMapEntry({
          key: result.program.id,
          value: newProductCorrelationId,
        })
      );
    }

    productClicked({
      correlationId: newProductCorrelationId,
    });

    // Mobius Redirect will fire the OutcomeTracked APP_VIEWED event
    const { heclid } = await mobiusRedirect({
      idToken: getToken(),
      productCorrelationId: newProductCorrelationId,
      link: result?.url,
      trackingContextOverride: {
        formatType: 'app',
        formatSubtype: 'jo-grid',
      },
    });
    // add heclid to map for Mobius track if API call succeeds
    if (result?.program?.id) {
      dispatch(setHeclidMapEntry({ key: result.program.id, value: heclid }));
    }

    setLoading(false);
    setRecommenderStep('PII_CONFIRMATION');
    // }
  };

  // Matches State properties
  const submissions = useSelector(selectSubmissions);
  const isProgramSubmitted = submissions.some((s) => s.program.id === result.program.id);
  const isSchoolSubmitted = !!submissions?.find((submission) => submission.school.id === result?.school.id);
  const handleProductClick = () => {
    // If school is already submitted, show duplicate popup
    if (isSchoolSubmitted) {
      dispatch(
        setLeadDelivery({
          show: true,
          currentPrograms: [result],
          showExitStep: true,
        })
      );
      dispatch(setShowDuplicatePopup(true));
      return;
    }

    // If program hasn't been submitted, show lead delivery slide-out
    const productCorrelationId = uuid();
    dispatch(setProductCorrelationIdMapEntry({ key: result.program.id, value: productCorrelationId }));
    productClicked({ correlationId: productCorrelationId });

    dispatch(
      setLeadDelivery({
        show: true,
        currentPrograms: [result],
        showExitStep: false,
      })
    );
  };

  useEffect(() => {
    const getApplicationCtaData = async () => {
      const response = await mobiusGetApplicationCta({
        programId: `${result.program.id}`,
        heclid: heclidMap[result.program.id],
        correlationId: productCorrelationIdMap[result.program.id],
      });
      setApplicationCtaData({ label: response?.label, url: response?.url });
    };

    getApplicationCtaData();
  }, []);

  const handleClick = () => {
    if (isProgramSubmitted && applicationCtaData?.url) {
      return window.open(applicationCtaData.url, '_blank');
    }
    if (onRecommender) {
      return onBtnClickHandler();
    }
    return handleProductClick();
  };

  return (
    <section
      ref={cardRef}
      className={cn(styles.card, fullWidth ? styles.fullWidthCard : undefined)}
      onClick={handleClick}
    >
      <div className={styles.imageContainer}>
        <div className={styles.bestMatchWrapper}>
          <span className={styles.thumbsIconContainer}>
            <FaThumbsUp size={11} color="var(--tertiary-800)" />
          </span>
          <span>Best Match</span>
        </div>
        <img className={styles.heroImage} src={schoolConfig?.image} alt={result?.school.name} />
        <div className={styles.logoContainer}>
          <img
            src={
              result?.school?.slug
                ? `https://simple-storage-server.highereducation.com/${result?.school.slug}.png?w=130&h=80`
                : undefined
            }
            alt={result?.school?.name}
          />
        </div>
        {/* <ProductTags decisionFactors={inputs?.['decision-factors']} /> */}
      </div>
      <div className={styles.bodyContainer}>
        <div className={styles.cardHeader}>
          <div>
            <h4 className={styles.title}>{result?.school?.name}</h4>
            {result?.school?.city && result?.school?.state && (
              <div className={styles.location}>
                <span className={styles.locationIcon}>
                  <HiLocationMarker size={16} />
                </span>{' '}
                {result?.school.city}, {result?.school.state}
                <div className={styles.programDetails}>
                  <span>&bull;</span>
                  <ProductFlags flags={result?.school?.additional} />
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={styles.cardBody}>
          <h3 className={styles.programName}>{result?.program?.name}</h3>
          <div className={styles.programDetails}>
            <ProductFlags flags={result?.school?.additional} />
          </div>
          {!isProgramSubmitted ? (
            <button
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCta}
            >
              Explore Program
            </button>
          ) : (
            <button
              data-testid={`recommender-popup-card-button-${position}`}
              type="button"
              className={styles.cardBtnCtaSubmitted}
            >
              {applicationCtaData?.label || 'Start Your Application'}
            </button>
          )}
          <a href="#more-matches" className={styles.viewMoreMatches}>
            View more matches
          </a>
          {isComparisonTest && (
            <span className={styles.comparisonCheckboxWrapperDesktop}>
              <ComparisonCheckbox result={result} />
            </span>
          )}
        </div>
      </div>
    </section>
  );
};

// We use memo to prevent rerenders when props and state haven't changed
export default memo(ResultHighlightCard);
